import { z } from 'zod';

const phoneSchema = z
  .string()
  .regex(
    /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
    'Please enter a valid phone number (e.g., (555) 555-5555 or 555-555-5555)',
  );

const emailSchema = z.string().email('Please enter a valid email address');

const ssnSchema = z
  .string()
  .regex(/^(?:\d{3}-\d{2}-\d{4}|\d{9})$/, 'Please enter a valid SSN (XXX-XX-XXXX)');

const ssn4DigitSchema = z.string().regex(/^\d{4}$/, 'Please enter the last 4 digits of your SSN');

const currencySchema = z.string().regex(/^\d+(\.\d{0,2})?$/, 'Please enter a valid dollar amount');

const heightSchema = z
  .string()
  .regex(
    /^(\d{1}'\d{1,2}"|\d{1,3}")$/,
    'Please enter height in feet\'inches" format (e.g., 5\'10")',
  )
  .refine(val => {
    // Convert to inches for comparison
    let totalInches = 0;
    if (val.includes("'")) {
      const parts = val.split("'");
      if (parts.length !== 2) return false;
      const feet = parseInt(parts[0]);
      const inchesStr = parts[1].replace('"', '');
      const inches = parseInt(inchesStr);
      if (isNaN(feet) || isNaN(inches) || inches >= 12) return false;
      totalInches = feet * 12 + inches;
    } else {
      const inchesStr = val.replace('"', '');
      totalInches = parseInt(inchesStr);
      if (isNaN(totalInches)) return false;
    }
    return totalInches >= 12 && totalInches <= 108;
  }, 'Height must be between 1\'0" and 9\'0" (inches should be less than 12)');

const weightSchema = z
  .string()
  .regex(/^\d{1,4}$/, 'Please enter weight in pounds')
  .refine(val => {
    const weight = parseInt(val);
    return weight >= 5 && weight <= 1500;
  }, 'Weight must be between 5 and 1500 pounds');

const ageSchema = z
  .string()
  .regex(/^\d{1,3}$/, 'Please enter a valid age')
  .refine(val => {
    const age = parseInt(val);
    return age >= 2 && age <= 150;
  }, 'Age must be between 2 and 150');

const zipcodeSchema = z
  .string()
  .regex(/^(\d{5}(-\d{4})?|\d{9})$/, 'Please enter a valid ZIP code (12345 or 12345-6789)');

const addressSchema = z.string().min(5, 'Please enter a complete address');

const streetAddressSchema = z
  .string()
  .min(3, 'Please enter a valid street address')
  .max(100, 'Street address is too long')
  .regex(
    /^[a-zA-Z0-9\s,.\-#/]+$/,
    'Street address can only contain letters, numbers, spaces, and common punctuation',
  );

const citySchema = z
  .string()
  .min(2, 'Please enter a valid city name')
  .max(50, 'City name is too long')
  .regex(
    /^[a-zA-Z\s\-'.]+$/,
    'City name can only contain letters, spaces, hyphens, and apostrophes',
  );

const stateSchema = z
  .string()
  .length(2, 'Please enter a valid 2-letter state code (e.g., CA, NY)')
  .regex(/^[A-Z]{2}$/, 'State must be a 2-letter code in uppercase (e.g., CA, NY)');

const npnSchema = z.string().min(2, 'Please enter a valid NPN');

const numberOnlySchema = z
  .string()
  .regex(/^\d+$/, 'Please enter only numbers');

const cityStateZipcodeSchema = z
  .string()
  .refine(val => {
    const parts = val.split(',').map(p => p.trim());
    if (parts.length !== 3) return false;
    const [city, state, zipcode] = parts;
    if (!city || city.length < 2) return false;
    if (!/^[A-Z]{2}$/i.test(state)) return false;
    if (!/^\d{5}$/.test(zipcode)) return false;
    return true;
  }, 'Please enter in format: City, ST, 12345');

export const validationSchemas = {
  phone: phoneSchema,
  email: emailSchema,
  ssn: ssnSchema,
  ssn4Digit: ssn4DigitSchema,
  currency: currencySchema,
  height: heightSchema,
  weight: weightSchema,
  age: ageSchema,
  zipcode: zipcodeSchema,
  address: addressSchema,
  streetAddress: streetAddressSchema,
  city: citySchema,
  state: stateSchema,
  npn: npnSchema,
  numberOnly: numberOnlySchema,
  cityStateZipcode: cityStateZipcodeSchema,
} as const;

export const validateField = (
  value: string,
  validationType: keyof typeof validationSchemas,
): { isValid: boolean; error?: string } => {
  try {
    validationSchemas[validationType].parse(value);
    return { isValid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { isValid: false, error: error.errors[0]?.message };
    }
    return { isValid: false, error: 'Invalid value' };
  }
};

// Re-export formatters from shared package
export { formatters } from '@shared/policy-application/formatters';

export const unformatters = {
  phone: (value: string) => value, // Keep phone as-is with formatting
  ssn: (value: string) => value, // Keep SSN as-is with hyphens
  currency: (value: string) => value.replace(/[^0-9.]/g, ''),
  zipcode: (value: string) => value.replace(/\D/g, ''),
  state: (value: string) => value.toUpperCase(),
  city: (value: string) => value,
  email: (value: string) => value,
  height: (value: string) => value,
  weight: (value: string) => value,
  age: (value: string) => value,
  address: (value: string) => value,
  streetAddress: (value: string) => value,
  ssn4Digit: (value: string) => value,
  npn: (value: string) => value,
  numberOnly: (value: string) => value, // Keep as-is to allow validation to catch non-numeric input
  cityStateZipcode: (value: string) => value,
};
